import { useEffect } from 'react';
import { useCart } from '@/context/CartContext';
import { loadPendingSistecredito, clearPendingSistecredito } from '@/lib/pendingPayment';
import { lookupOrder, isSistecreditoOrderApproved } from '@/services/orderTracking';

/**
 * Sincroniza los intentos de financiamiento Sistecrédito que quedaron
 * pendientes.
 *
 * Cuando el cliente sale al portal de Sistecrédito y vuelve a la tienda (por la
 * página de pedidos o por cualquier otra ruta), consultamos el estado REAL del
 * pedido en el servidor y:
 *   - Si el financiamiento quedó APROBADO: vaciamos del carrito únicamente los
 *     productos de ese pedido y descartamos el intento (paridad con ePayco).
 *   - Si fue RECHAZADO/CANCELADO: descartamos el intento, pero conservamos el
 *     carrito para que el cliente pueda reintentar con otro medio.
 *   - Si sigue PENDIENTE: no tocamos nada y volvemos a comprobar más tarde.
 *
 * Nunca crea pedidos: solo sincroniza el carrito con lo que ya existe.
 */
export default function SistecreditoPendingSync() {
  const { removeItems, clearCart } = useCart();

  useEffect(() => {
    let running = false;

    async function reconcile() {
      if (running) return;
      const pending = loadPendingSistecredito();
      if (!pending) return;
      running = true;
      try {
        const res = await lookupOrder(pending.order.orderNumber, pending.contact);
        if (!res.found || !res.order) return;
        if (isSistecreditoOrderApproved(res.order)) {
          if (pending.orderedKeys?.length) removeItems(pending.orderedKeys);
          else clearCart();
          clearPendingSistecredito();
        } else if (res.order.financingDeclined || res.order.isCancelled) {
          clearPendingSistecredito();
        }
      } catch {
        // Sin conexión: se reintentará en el próximo enfoque/intervalo.
      } finally {
        running = false;
      }
    }

    void reconcile();

    const onFocus = () => {
      if (document.visibilityState === 'visible') void reconcile();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    // Mientras haya un intento pendiente, reintentamos cada 30 s para reflejar
    // la aprobación aunque el cliente se quede con la pestaña abierta.
    const interval = window.setInterval(onFocus, 30000);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      window.clearInterval(interval);
    };
  }, []);

  return null;
}